import React from 'react';
import { Formik, useFormikContext } from 'formik';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import CancelButton from '~/components/core/Buttons/CancelButton';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import Text from '~/components/core/TextComponents/Text';
import { useFormikValidationErrorToast } from '~/components/hooks/useFormikValidationErrorToast';
import { TextFieldFormik } from '~/components/TextFieldFormik';
import type { N8NConfigurationModel } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import {
  createN8nConfigurationValidationSchema,
  getInitialValues,
  type N8nConfigurationFormValues,
} from './utils/validation';

interface N8nConfigurationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: N8nConfigurationFormValues) => Promise<void>;
  configuration?: N8NConfigurationModel;
  isEdit?: boolean;
}

const N8nConfigurationDialogInner: React.FC<{
  onClose: () => void;
  isEdit?: boolean;
}> = ({ onClose, isEdit = false }) => {
  const { isSubmitting, handleSubmit, values } = useFormikContext<N8nConfigurationFormValues>();
  const { classes } = useStyles();

  useFormikValidationErrorToast({ toastId: 'n8n-configuration-dialog' });

  const title = isEdit ? 'Edit N8n Configuration' : 'Create N8n Configuration';
  const isFieldsDisabled = isSubmitting || !values.is_enabled;

  return (
    <CardDialog isDialog title={title} onClose={onClose} preventClose={isSubmitting} maxWidth="md" fullWidth>
      <Grid container direction="column" spacing={4}>
        <Grid item xs={12}>
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.PRIMARY}>
            Configure your n8n workflow engine integration settings. Enable the integration and set the timeout for
            workflow execution. Base URL and API key are configured at the environment level.
          </Text>
        </Grid>

        <Grid container item spacing={3}>
          {/* Enable/Disable Switch */}
          <Grid item xs={12}>
            <SwitchFormik id="is_enabled" label="Enable N8n Integration" disabled={isSubmitting} />
          </Grid>

          {/* Timeout */}
          <Grid item xs={12} md={6}>
            <TextFieldFormik
              id="timeout_seconds"
              label="Timeout (seconds)"
              fullWidth
              disabled={isFieldsDisabled}
              className={classes.textField}
              inputProps={{
                min: 1,
                max: 300,
              }}
            />
          </Grid>
        </Grid>

        <div className={classes.buttonsContainer}>
          <CancelButton disabled={isSubmitting} onClick={onClose} />
          <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
            {isEdit ? 'UPDATE' : 'SAVE'}
          </Button>
        </div>
      </Grid>
    </CardDialog>
  );
};

const N8nConfigurationDialog: React.FC<N8nConfigurationDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  configuration,
  isEdit = false,
}) => {
  if (!isOpen) {
    return null;
  }

  const handleFormSubmit = async (
    values: N8nConfigurationFormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      await onSubmit(values);
      onClose();
    } catch (error) {
      reportAxiosError(error);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={getInitialValues(configuration)}
      validationSchema={createN8nConfigurationValidationSchema()}
      enableReinitialize
      onSubmit={handleFormSubmit}
    >
      <N8nConfigurationDialogInner onClose={onClose} isEdit={isEdit} />
    </Formik>
  );
};

export default N8nConfigurationDialog;
